import type { LegacyShellData } from "@/types/domain";

const trimTrailingSlash = (path: string): string => (path.length > 1 ? path.replace(/\/+$/, "") : path);

const normalise = (path: string): string => trimTrailingSlash(path.split(/[?#]/)[0]).toLowerCase();

const isPageRule = (rule: string): boolean => /\.[a-z0-9]+$/i.test(rule);

const matchesRule = (path: string, rule: string): boolean => {
  if (isPageRule(rule)) return path === rule;
  return path === rule || path.startsWith(rule + "/");
};

export const isLegacyShellPath = (pathname: string, legacyShell?: LegacyShellData | null): boolean => {
  if (!legacyShell?.enabled || !legacyShell.paths?.length) return false;
  const path = normalise(pathname);
  if (path === "" || path === "/") return false;
  return legacyShell.paths
    .map(normalise)
    .filter((rule) => rule !== "" && rule !== "/")
    .some((rule) => matchesRule(path, rule));
};

export const toSameOriginPath = (href: string, base: string = window.location.href): string | null => {
  try {
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
};

export const LEGACY_SHELL_BYPASS_PARAM = "legacyShell";
export const LEGACY_SHELL_BYPASS_VALUE = "0";
export const LEGACY_FRAME_STATE_KEY = "legacyFrame";

const splitPath = (path: string): { base: string; query: URLSearchParams; hash: string } => {
  const hashIndex = path.indexOf("#");
  const hash = hashIndex < 0 ? "" : path.slice(hashIndex);
  const rest = hashIndex < 0 ? path : path.slice(0, hashIndex);
  const queryIndex = rest.indexOf("?");
  return {
    base: queryIndex < 0 ? rest : rest.slice(0, queryIndex),
    query: new URLSearchParams(queryIndex < 0 ? "" : rest.slice(queryIndex + 1)),
    hash,
  };
};

const joinPath = (base: string, query: URLSearchParams, hash: string): string => {
  const search = query.toString();
  return base + (search ? "?" + search : "") + hash;
};

export const hasShellBypass = (path: string): boolean =>
  splitPath(path).query.get(LEGACY_SHELL_BYPASS_PARAM) === LEGACY_SHELL_BYPASS_VALUE;

export const withShellBypass = (path: string): string => {
  const { base, query, hash } = splitPath(path);
  query.set(LEGACY_SHELL_BYPASS_PARAM, LEGACY_SHELL_BYPASS_VALUE);
  return joinPath(base, query, hash);
};

export const withoutShellBypass = (path: string): string => {
  if (!hasShellBypass(path)) return path;
  const { base, query, hash } = splitPath(path);
  query.delete(LEGACY_SHELL_BYPASS_PARAM);
  return joinPath(base, query, hash);
};

export const isPopupWindow = (win: Pick<Window, "opener">): boolean => !!win.opener;

const BREAKOUT_KEY = "vecta:legacy-breakout";
const BREAKOUT_WINDOW_MS = 5000;

// Allows one break-out per URL in a short window so a framed page that keeps bouncing to the top cannot loop
export const claimFrameBreakOut = (storage: Pick<Storage, "getItem" | "setItem">, url: string, now: number = Date.now()): boolean => {
  try {
    const previous = JSON.parse(storage.getItem(BREAKOUT_KEY) ?? "null") as { url?: string; at?: number } | null;
    if (previous?.url === url && typeof previous.at === "number" && now - previous.at < BREAKOUT_WINDOW_MS) return false;
    storage.setItem(BREAKOUT_KEY, JSON.stringify({ url, at: now }));
  } catch {
    return true;
  }
  return true;
};

export const LEGACY_SHELL_COOKIE = "vecta-legacy-shell";

export const setLegacyShellOverride = (enabled: boolean, doc: Pick<Document, "cookie"> = document): void => {
  doc.cookie = `${LEGACY_SHELL_COOKIE}=${enabled ? "1" : "0"}; path=/; max-age=31536000; samesite=lax`;
};

export const replaceLocation = (url: string): void => window.location.replace(url);
